import { useStowageStore } from 'src/context/StowageContext';
import useApiEndpoints from 'src/context/useApiEndpoints';
import useApi from '../useApi';
import { useEffect } from 'react';
import { StowagePlanBM } from 'src/types/BusinessModel/Stowage/StowagePlanBM';
import apiEndpointUtils from 'src/utils/apiEndpointUtils';
import { mapStowagePlans } from 'src/factory/Stowage/StowagePlanFactory';
import { buildQueryParams } from '../endpoints/queryParamsHelper';
import { getStowageDataItems } from 'src/utils/stowage/stowagePlanChangeUtils/stowageDataItemsBuilder';
import useGetStowageApi from './useGetStowageApi';
import useLoadBaseStowageData from 'src/hooks/stowage/useLoadBaseStowageData';
import { updateStowageDataSimple } from 'src/utils/stowage/updateStowageDataUtils';
import { Vessel } from 'src/types/ViewModel/Vessel/Vessel';
import { getMasterStowagePlan, fillMissingImosSyncDateFromParents } from 'src/utils/stowage/stowagePlanUtils';

export default () => {
    const { apiEndpoints } = useApiEndpoints();
    const { state, getApiData } = useApi();
    const { setLoadedMasterStowagePlan } = useStowageStore();
    const { getStowagePlanChanges } = useGetStowageApi();
    const { processApiEndpointError } = apiEndpointUtils();
    const { loadBaseData } = useLoadBaseStowageData();

    const getMasterStowagePlanFromApi = async (vessel: Vessel) => {
        const imoNumber = vessel.imoNumber;
        // First load base stowage data
        const baseDataResult = await loadBaseData();
        if (!baseDataResult.success) {
            console.error('Failed to load base stowage data:', baseDataResult.error);
            return { success: false, error: baseDataResult.error };
        }

        try {
            const result = await getApiData({
                endpoint: apiEndpoints['getMasterStowagePlan'].url,
                queryParam: buildQueryParams({ imoNumber }),
                additionalInfo: { imoNumber },
            });

            const bms = result as StowagePlanBM[];
            const _stowagePlans = mapStowagePlans(bms);
            const _masterStowagePlan = getMasterStowagePlan(_stowagePlans);

            fillMissingImosSyncDateFromParents(_masterStowagePlan, _stowagePlans);

            // Force cache refresh to ensure latest changes
            const stowagePlanChanges = await getStowagePlanChanges(_masterStowagePlan.stowagePlanGuid, true);
            const { stowagePlanRotationPortCalls, updatedStowagePlan } = getStowageDataItems(stowagePlanChanges, _stowagePlans, _masterStowagePlan);
            await updateStowageDataSimple(
                updatedStowagePlan,
                stowagePlanRotationPortCalls,
                updatedStowagePlan.cargoes,
                baseDataResult.data.products,
                baseDataResult.data.productCategories,
                baseDataResult.data.stowageRuleMaps,
                baseDataResult.data.portNames,
                baseDataResult.data.berthNames,
                vessel
            );
            setLoadedMasterStowagePlan({ ...updatedStowagePlan });

            return { success: true, data: updatedStowagePlan };
        } catch (error) {
            console.error('Error loading master stowage plan:', error);
            return { success: false, error };
        }
    };

    useEffect(() => {
        (async () => {
            if (state.status !== 'success') {
                processApiEndpointError(state);
            }
        })();
    }, [state]);

    return { getMasterStowagePlanFromApi };
};
